import { ExportType } from '@components'

import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'

import { useCsvExport } from '@features/exports/hooks/useCsvExport'
import { useXlsxExport } from '@features/exports/hooks/useXlsxExport'
import { ReportsExportButton } from '@views/reports/elements/ReportsExportButton'
import { useReportPdfExport } from '@views/reports/hooks/useReportPdfExport'

import { NotificationKeys } from '../../../../../../../enums/notificationKeys'
import { ReportsType } from '../../../../../enums/reportsType'
import { useProfitAndLossFilters } from '../../contexts/profitAndLossFiltersContext'
import { useProfitAndLossData } from '../../hooks/useProfitAndLossData'
import { useProfitAndLossSpreadSheetExportData } from '../../hooks/useProfitAndLossSpreadSheetExportData'

const MAX_PERIODS_FOR_EXPORT = 2

export const ProfitAndLossSideActions = () => {
  const { t } = useTranslation()

  const { reportData, isLoading: isLoadingReportData, allExpandableIds, periods } = useProfitAndLossData()
  const { appliedFilters } = useProfitAndLossFilters()
  const { spreadSheetData } = useProfitAndLossSpreadSheetExportData()

  const { exportAsPdf, isLoading: isLoadingPdfExport } = useReportPdfExport({
    allExpandableIds,
    appliedFilters,
    fileName: t('reports.export.file_name.profit_and_loss'),
    periods,
    reportData,
    reportType: ReportsType.ProfitAndLoss,
    title: t('reports.profit_and_loss'),
  })

  const { exportAsCsv, isLoading: isLoadingCsvExport } = useCsvExport({
    spreadSheetData,
    fileName: t('reports.export.file_name.profit_and_loss'),
    notificationKey: NotificationKeys.ReportsExportCsv,
  })

  const { exportAsXlsx, isLoading: isLoadingXlsxExport } = useXlsxExport({
    spreadSheetData,
    fileName: t('reports.export.file_name.profit_and_loss'),
    notificationKey: NotificationKeys.ReportsExportXlsx,
  })

  const handleSelect = useCallback(
    (_: string, type: ExportType) => {
      switch (type) {
        case ExportType.Pdf:
          return exportAsPdf()
        case ExportType.Csv:
          return exportAsCsv()
        case ExportType.Xlsx:
          return exportAsXlsx()
      }
    },
    [exportAsCsv, exportAsPdf, exportAsXlsx],
  )

  const isPeriodLimitExceeded = (periods?.length || 0) > MAX_PERIODS_FOR_EXPORT

  const isLoading = isLoadingReportData || isLoadingPdfExport || isLoadingCsvExport || isLoadingXlsxExport

  return (
    <ReportsExportButton
      disabled={isLoading}
      loading={isLoading}
      isPdfItemDisabled={isPeriodLimitExceeded}
      onSelect={handleSelect}
      pdfTooltipLabel={t('reports.export.pdf.tooltip.profit_and_loss')}
    />
  )
}
